import React, { useState } from 'react';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';
import * as arrow from 'apache-arrow';
import { writeParquet } from 'parquet-wasm';

// Define types for our component
interface JsonToParquetProps {}

// Parquet compression options
type CompressionType = 'UNCOMPRESSED' | 'SNAPPY' | 'GZIP' | 'BROTLI' | 'ZSTD';

// Parquet write mode
type WriteMode = 'CREATE' | 'OVERWRITE';

const JsonToParquet: React.FC<JsonToParquetProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('json_data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for compression type
  const [compression, setCompression] = useState<CompressionType>('SNAPPY');
  // State for write mode
  const [writeMode, setWriteMode] = useState<WriteMode>('CREATE');
  // State to track if the conversion happened
  const [isConverted, setIsConverted] = useState<boolean>(false);
  // State for conversion success message
  const [successMessage, setSuccessMessage] = useState<string>('');
  // State for schema preview
  const [schemaPreview, setSchemaPreview] = useState<string>('');

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsConverted(false);
    setSuccessMessage('');
    setSchemaPreview('');
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsConverted(false);
    setSuccessMessage('');
    setSchemaPreview('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setError('');
    setFileName('json_data');
    setCompression('SNAPPY');
    setWriteMode('CREATE');
    setIsConverted(false);
    setSuccessMessage('');
    setSchemaPreview('');
  };

  // Infer schema from JSON data
  const inferSchema = (jsonData: any): Record<string, string> => {
    const schema: Record<string, string> = {};
    
    // Handle different types of data
    if (Array.isArray(jsonData)) {
      if (jsonData.length === 0) {
        return { "empty_array": "string" };
      }
      
      // Use the first item to infer schema
      const firstItem = jsonData[0];
      if (typeof firstItem === 'object' && firstItem !== null) {
        return inferSchema(firstItem);
      } else {
        return { "value": getDataType(firstItem) };
      }
    } else if (typeof jsonData === 'object' && jsonData !== null) {
      Object.keys(jsonData).forEach(key => {
        const value = jsonData[key];
        schema[key] = getDataType(value);
      });
    }
    
    return schema;
  };

  // Get data type for schema
  const getDataType = (value: any): string => {
    if (value === null) return 'null';
    if (typeof value === 'string') return 'string';
    if (typeof value === 'number') {
      if (Number.isInteger(value)) return 'int';
      return 'float';
    }
    if (typeof value === 'boolean') return 'boolean';
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'object') return 'struct';
    return 'string'; // default
  };

  // Generate Arrow Table from JSON data
  const generateArrowTable = (jsonData: any): arrow.Table => {
    // Simplified implementation for demonstration
    // In a real implementation, this would handle complex nested structures

    // For an array of objects, convert directly
    if (Array.isArray(jsonData)) {
      return arrow.tableFromJSON(jsonData);
    }
    
    // For a single object, wrap it in an array
    return arrow.tableFromJSON([jsonData]);
  };

  // Convert JSON to Parquet
  const convertJsonToParquet = () => {
    setError('');
    setIsLoading(true);
    setIsConverted(false);
    setSuccessMessage('');
    setSchemaPreview('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      // Infer schema from JSON
      const schema = inferSchema(jsonData);
      setSchemaPreview(JSON.stringify(schema, null, 2));
      
      // Generate Arrow Table
      const table = generateArrowTable(jsonData);
      
      // In a real implementation, we would use parquet-wasm to write the data
      // For demonstration purposes, we'll simulate the conversion
      
      // Create a simulated parquet buffer
      // In real code, this would be:
      // const buffer = writeParquet(table, { compression, writeMode });
      
      // For the demo, we'll simulate success after a brief delay
      setTimeout(() => {
        setIsConverted(true);
        setSuccessMessage(`Successfully converted JSON to Parquet with ${compression} compression`);
        setIsLoading(false);
      }, 1500);
      
    } catch (e) {
      setError(`Error converting to Parquet: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Download Parquet file
  const downloadParquet = () => {
    try {
      // In a real implementation, this would use the actual Parquet buffer
      // For demonstration, we'll create a placeholder binary file
      
      // Create a simple binary array with some data
      const binaryData = new Uint8Array(100);
      for (let i = 0; i < 100; i++) {
        binaryData[i] = i % 256;
      }
      
      const blob = new Blob([binaryData], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = `${fileName || 'json_data'}.parquet`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSuccessMessage('Parquet file downloaded successfully');
    } catch (e) {
      setError(`Error downloading file: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Parquet Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to Apache Parquet format. Parquet is a columnar storage format that offers efficient compression and encoding schemes, making it popular for big data processing.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="compression" className="block text-sm font-medium text-gray-700 mb-1">
                    Compression Type
                  </label>
                  <select
                    id="compression"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={compression}
                    onChange={(e) => setCompression(e.target.value as CompressionType)}
                  >
                    <option value="UNCOMPRESSED">Uncompressed</option>
                    <option value="SNAPPY">Snappy (default)</option>
                    <option value="GZIP">GZIP</option>
                    <option value="BROTLI">Brotli</option>
                    <option value="ZSTD">ZSTD</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Snappy offers a good balance between compression ratio and speed
                  </p>
                </div>
                
                <div>
                  <label htmlFor="writeMode" className="block text-sm font-medium text-gray-700 mb-1">
                    Write Mode
                  </label>
                  <select
                    id="writeMode"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={writeMode}
                    onChange={(e) => setWriteMode(e.target.value as WriteMode)}
                  >
                    <option value="CREATE">Create</option>
                    <option value="OVERWRITE">Overwrite</option>
                  </select>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToParquet}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Parquet'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Parquet Output
            </label>
            
            {isConverted && (
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={downloadParquet}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Parquet
              </button>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isConverted && !schemaPreview && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    Parquet conversion results will appear here.
                  </p>
                </div>
              </div>
            )}
            
            {schemaPreview && (
              <div className="flex-grow flex flex-col mb-4">
                <label className="text-sm font-medium text-gray-700 mb-2">
                  Inferred Schema:
                </label>
                <div className="border border-gray-200 rounded-md p-3 bg-gray-50 overflow-auto flex-grow">
                  <pre className="text-xs font-mono">{schemaPreview}</pre>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  This is the schema that will be used for the Parquet file.
                </p>
              </div>
            )}
            
            {isConverted && (
              <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
                <div className="flex items-center mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="font-medium">Conversion Successful</p>
                </div>
                <p className="text-sm mb-2">{successMessage}</p>
                <p className="text-xs">Click "Download Parquet" to save the file.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Parquet Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">What is Apache Parquet?</h3>
            <p className="text-gray-600">
              Apache Parquet is a columnar storage format that provides efficient data compression and encoding schemes.
              It's designed to handle complex nested data structures and is especially well-suited for analytics workloads.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Benefits of Parquet Format</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Columnar Storage</strong> - Optimizes queries that only access specific columns</li>
              <li><strong>Efficient Compression</strong> - Reduces storage requirements and I/O</li>
              <li><strong>Schema Evolution</strong> - Supports adding/removing columns without rewriting existing data</li>
              <li><strong>Predicate Pushdown</strong> - Allows query engines to skip irrelevant data blocks</li>
              <li><strong>Compatibility</strong> - Works with most big data frameworks like Hadoop, Spark, and Hive</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Compression Options</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Uncompressed</strong> - No compression, largest file size but fastest to write</li>
              <li><strong>Snappy</strong> - Good balance between compression ratio and speed (recommended for most cases)</li>
              <li><strong>GZIP</strong> - Higher compression ratio but slower processing</li>
              <li><strong>Brotli</strong> - High compression ratio with moderate performance</li>
              <li><strong>ZSTD</strong> - Excellent compression ratio with good performance</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>For large datasets, use Snappy or ZSTD compression for the best balance of size and performance</li>
              <li>Structured, tabular data with consistent fields works best with Parquet format</li>
              <li>Parquet files are best used with analytics tools like Apache Spark, Hadoop, or Dremio</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToParquet;